import React, {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useGroupContext } from "groopit-js/GroupContext";
import {
  AsyncStatus,
  CreateInsightReportDTO,
  ErrorDTO,
  ErrorType,
  GroopitFilterAssignmentDTO,
  GroopitFilterDisplayDTO,
  PatternsResultDTO,
  SummarizeResultDTO,
} from "groopit-js/client";

import useLatestRef from "../react/react-use/useLatestRef";
import { useEffectOnce, useInterval } from "react-use";

import { useGroopitApiContext } from "groopit-js";

import { AiBackgroundProcessingContext } from "./AiBackgroundProcessingContext";
import { FakeStreamingMarkdown } from "./ChatWithYourData/FakeStreamingMarkdown";
import { FilterSummary } from "../FeedFilter/FilterSummary";
import { FilterPanelTitle } from "../FeedFilter/FilterPanel";
import { GroopitFilterDisplay } from "../Models/GroopitFilterDisplay";
import { TypingIndicator } from "../Icons/TypingIndicator";

const ReportGenerationError = () => {
  return (
    <>
      <div className="font-bold">Limit Exceeded</div>
      <div>
        You&apos;ve exceeded AI&apos;s limit to generate insights. Reduce the
        dataset using {FilterPanelTitle} settings and try again.
      </div>
    </>
  );
};

interface AiSummaryInsightGenerationPanelProps {
  filter: GroopitFilterDisplayDTO;
  bottomPadding: string;
  scrollContainer?: RefObject<HTMLElement>;
  onReportFinished: (report: CreateInsightReportDTO) => void;
  onReportError?: (error: ErrorDTO) => void;
}

export const AiSummaryInsightGenerationPanel = (
  props: AiSummaryInsightGenerationPanelProps,
) => {
  const { bottomPadding, onReportFinished, onReportError } = props;
  const api = useLatestRef(useGroopitApiContext());
  const { defaultPollingIntervalMS } = useContext(
    AiBackgroundProcessingContext,
  );
  const { group } = useGroupContext();
  const [summaryResult, setSummaryResult] = useState<SummarizeResultDTO>();
  const [patternsResult, setPatternsResult] = useState<PatternsResultDTO>();
  const [combinedReport, setCombinedReport] =
    useState<CreateInsightReportDTO>();
  const [summaryContent, setSummaryContent] = useState<string>();
  const [patternsContent, setPatternsContent] = useState<string>();
  const [error, setError] = useState<ErrorDTO>();
  // Capture the filter and the groopit count on mount because
  // these can't change once the generation starts
  const [filterIn] = useState<GroopitFilterDisplayDTO>(props.filter);
  const filterUsedForReport = useMemo(() => {
    const filter = GroopitFilterDisplay.convertToGroopitFilter(filterIn);
    // We're making two separate calls and combining the
    // results so lock our endDate down so the data used
    // is consistent across the calls.
    if (!filter.endDate) {
      // Setting a global end date is easier, but that's
      // considered a "legacy" filter that isn't editable
      // in the UI so we need to apply the end date to
      // each data model individually. (Unless it also
      // has a legacy start date in which case we can just
      // let it stay legacy)
      const endDate = new Date();
      if (filter.startDate) {
        filter.endDate = endDate;
      } else if (filter.assignments?.length) {
        filter.assignments.forEach((assignment) => {
          assignment.endDate = endDate;
        });
      } else if (group.assignments) {
        filter.assignments = group.assignments.map(
          (assignment) =>
            new GroopitFilterAssignmentDTO({
              id: assignment.id,
              endDate: endDate,
            }),
        );
      }
    }
    return filter;
  }, [filterIn, group.assignments]);

  useInterval(
    () => {
      // The interval should pause when result is non-null but make
      // TypesScript happy
      if (
        summaryResult?.status === AsyncStatus.InProgress &&
        summaryResult.jobId
      ) {
        api.current.insights
          .getSummarizeResult(summaryResult.jobId, group.id)
          .then(setSummaryResult);
      }
    },
    summaryResult?.status === AsyncStatus.InProgress
      ? defaultPollingIntervalMS
      : null,
  );

  // We technically run the calls in parallel, but we display the results
  // in sequence so there's no point in starting the interval until the
  // summary completes
  useInterval(
    () => {
      // The interval should pause when result is non-null but make
      // TypesScript happy
      if (
        patternsResult?.status === AsyncStatus.InProgress &&
        patternsResult.jobId
      ) {
        api.current.insights
          .getPatternsResult(patternsResult.jobId, group.id)
          .then(setPatternsResult);
      }
    },
    summaryResult?.status === AsyncStatus.Completed &&
      patternsResult?.status === AsyncStatus.InProgress
      ? defaultPollingIntervalMS
      : null,
  );

  const sendPrompt = useCallback(() => {
    api.current.insights
      .summarize(group.id, filterUsedForReport)
      .then(setSummaryResult)
      .catch((error) =>
        setError(
          new ErrorDTO({
            error: ErrorType.Error,
            error_description: JSON.stringify(error),
          }),
        ),
      );

    api.current.insights
      .identifyPatterns(group.id, filterUsedForReport)
      .then(setPatternsResult)
      .catch((error) =>
        setError(
          new ErrorDTO({
            error: ErrorType.Error,
            error_description: JSON.stringify(error),
          }),
        ),
      );
  }, [api, filterUsedForReport, group.id]);

  useEffect(() => {
    // Setting the combined report should trigger the transition
    // to the viewer/editor so don't bother doing anything here
    // if the report is already set
    if (
      summaryResult?.status === AsyncStatus.Completed &&
      patternsResult?.status === AsyncStatus.Completed &&
      !combinedReport
    ) {
      // These are only nullable because they are not set in
      // the case of an error. They should always be set in success.
      // They should be the same but err on picking the lower of the
      // two.
      const groopitCount = Math.min(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        summaryResult.groopitCount!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        patternsResult.groopitCount!,
      );
      const containsHiddenData =
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        summaryResult.containsHiddenData! || patternsResult.containsHiddenData!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const headline = summaryResult.headline!;
      setCombinedReport(
        new CreateInsightReportDTO({
          aggregatedDetails: summaryResult.aggregatedDetails,
          timestamp: new Date(),
          filter: filterUsedForReport,
          groopitCount: groopitCount,
          includesHiddenData: containsHiddenData,
          keyFindings: summaryResult.keyFindings,
          title: headline,
          summary: summaryResult.summary,
          patterns: patternsResult.patterns,
        }),
      );
    } else if (summaryResult?.status === AsyncStatus.Failed) {
      setError(summaryResult.error);
    } else if (patternsResult?.status === AsyncStatus.Failed) {
      setError(patternsResult.error);
    }
  }, [filterUsedForReport, summaryResult, patternsResult, combinedReport]);

  useEffect(() => {
    if (combinedReport) {
      onReportFinished(combinedReport);
    }
  }, [combinedReport, onReportFinished]);

  useEffect(() => {
    if (error) {
      onReportError?.(error);
    }
  }, [error, onReportError]);

  useEffectOnce(() => {
    sendPrompt();
  });

  useEffect(() => {
    if (summaryResult && summaryResult.status === AsyncStatus.Completed) {
      setSummaryContent(
        `## Headline:\n${summaryResult.headline}\n\n${summaryResult.summary}`,
      );
    }
  }, [summaryResult]);

  useEffect(() => {
    if (
      summaryResult &&
      summaryResult.status === AsyncStatus.Completed &&
      patternsResult &&
      patternsResult.status === AsyncStatus.Completed
    ) {
      setPatternsContent(
        `## Patterns:\n\n${patternsResult.patterns?.join(
          "\n\n",
        )}\n\n${summaryResult?.keyFindings}\n\n${summaryResult?.aggregatedDetails}`,
      );
    }
  }, [patternsResult, summaryResult]);

  const displaySummarySection = useCallback(() => {
    if (summaryContent) {
      return (
        <FakeStreamingMarkdown
          content={summaryContent}
          shouldFakeStreaming={true}
        />
      );
    } else if (
      summaryResult &&
      summaryResult.status === AsyncStatus.InProgress
    ) {
      return (
        <>
          <div className="text-app-teal">Generating Headline and Summary.</div>
          <div className="place-self-start">
            <TypingIndicator />
          </div>
        </>
      );
    } else {
      return null;
    }
  }, [summaryContent, summaryResult]);

  const displayPatternsSection = useCallback(() => {
    if (patternsContent) {
      return (
        <FakeStreamingMarkdown
          content={patternsContent}
          shouldFakeStreaming={true}
          style={{ paddingBottom: bottomPadding }}
        />
      );
    } else if (
      patternsResult &&
      patternsResult.status === AsyncStatus.InProgress
    ) {
      return (
        <>
          <div className="text-app-teal">
            Generating Patterns, Key Findings, and Aggregated Details.
          </div>
          <div className="place-self-start">
            <TypingIndicator />
          </div>
        </>
      );
    } else {
      return null;
    }
  }, [bottomPadding, patternsContent, patternsResult]);

  return (
    <>
      <FilterSummary filter={filterIn} />
      {error ? (
        <ReportGenerationError />
      ) : (
        <>
          {displaySummarySection()}
          {displayPatternsSection()}
        </>
      )}
    </>
  );
};
